import { Stack } from "@mui/material";
import { useEffect, useMemo, useState } from "react";
import { useUserContext } from "../contexts/UserContext";
import DashboardBadge from "../reports/dashboard/DashboardBadge";
import { searchChecks } from "../reports/dashboard/utils/checkUtils";
import { searchEscrowOverdueOrders } from "../reports/dashboard/utils/escrowOverdueOrdersUtils";
import {
  NY_searchNonZeroLedgerBalances,
  searchEarnestDepositLedgers,
  searchNonZeroLedgerBalances
} from "../reports/dashboard/utils/nonZeroLedgerBalancesUtils";
import { searchPoliciesToSend } from "../reports/dashboard/utils/policiesToSendUtils";
import { searchPoliciesWithIssues } from "../reports/dashboard/utils/policiesWithIssuesUtils";
import { DashboardReportType } from "../types/reports";

export default function Dashboard({
  internalUserIds,
  permissions
}: {
  internalUserIds: number[];
  permissions: string[];
}) {
  const { isNyUser } = useUserContext();
  const [loading, setLoading] = useState<boolean>(true);
  const [checksCount, setChecksCount] = useState<number | null>(null);
  const [policiesCount, setPoliciesCount] = useState<number | null>(null);
  const [policiesToSendCount, setPoliciesToSendCount] = useState<number | null>(null);
  const [escrowOverdueOrdersCount, setEscrowOverdueOrdersCount] = useState<number | null>(null);
  const [nonZeroLedgerBalancesCount, setNonZeroLedgerBalancesCount] = useState<number | null>(null);
  const [dormantDepositsCount, setDormantDepositsCount] = useState<number | null>(null);
  const internalUserIdsKey = internalUserIds.join(",");
  const permissionsKey = permissions.join(",");
  const normalizedInternalUserIds = useMemo(
    () => (internalUserIdsKey ? internalUserIdsKey.split(",") : []),
    [internalUserIdsKey]
  );

  const filterItemsByInternalUsers = (items: any[]) => {
    return items.filter((item) => normalizedInternalUserIds.includes(String(item.assignedToUser?.id)));
  };
  const fetchChecks = async () => {
    const checks = await searchChecks();
    const filteredChecks = filterItemsByInternalUsers(checks);
    setChecksCount(filteredChecks.length);
  };

  const fetchPoliciesWithIssues = async () => {
    const policiesWithIssues = await searchPoliciesWithIssues();
    const filteredPoliciesWithIssues = filterItemsByInternalUsers(policiesWithIssues);
    setPoliciesCount(filteredPoliciesWithIssues.length);
  };

  const fetchPoliciesToSend = async () => {
    const policiesToSend = await searchPoliciesToSend();
    const filteredPoliciesToSend = filterItemsByInternalUsers(policiesToSend);
    setPoliciesToSendCount(filteredPoliciesToSend.length);
  };

  const fetchEscrowOverdueOrders = async () => {
    const escrowOverdueOrders = await searchEscrowOverdueOrders();
    const filteredEscrowOverdueOrders = filterItemsByInternalUsers(escrowOverdueOrders);
    setEscrowOverdueOrdersCount(filteredEscrowOverdueOrders.length);
  };

  const fetchNonZeroLedgerBalances = async () => {
    const nonZeroLedgerBalances = isNyUser
      ? await NY_searchNonZeroLedgerBalances()
      : await searchNonZeroLedgerBalances();
    const filteredNonZeroLedgerBalances = filterItemsByInternalUsers(nonZeroLedgerBalances);
    setNonZeroLedgerBalancesCount(filteredNonZeroLedgerBalances.length);
  };

  const fetchDormantDeposits = async () => {
    const earnestDepositLedgers = await searchEarnestDepositLedgers();
    const filteredDormantDeposits = filterItemsByInternalUsers(earnestDepositLedgers).filter((ledger) => ledger.isDormant);
    setDormantDepositsCount(filteredDormantDeposits.length);
  };

  useEffect(() => {
    const fetchData = async () => {
      if (normalizedInternalUserIds.length === 0) {
        return;
      }

      setLoading(true);
      try {
        const fetchPromises = [];
        if (permissions.includes("OutstandingChecks")) fetchPromises.push(fetchChecks());
        if (permissions.includes("PolicyIssues")) fetchPromises.push(fetchPoliciesWithIssues());
        if (permissions.includes("PoliciesToSend")) fetchPromises.push(fetchPoliciesToSend());
        if (permissions.includes("EscrowOverdue")) fetchPromises.push(fetchEscrowOverdueOrders());
        if (permissions.includes("NonZeroLedgerBalances")) fetchPromises.push(fetchNonZeroLedgerBalances());
        if (permissions.includes("Deposits")) fetchPromises.push(fetchDormantDeposits());

        await Promise.all(fetchPromises);
      } catch (error) {
        console.error("Error fetching dashboard data:", error);
      } finally {
        setLoading(false);
      }
    };

    fetchData();
  }, [internalUserIdsKey, permissionsKey, isNyUser]);

  return (
    <Stack direction="row" spacing={2} flexWrap="wrap" useFlexGap>
      {permissions.includes("NonZeroLedgerBalances") && (
        <DashboardBadge
          dashboardReportType={DashboardReportType.NonZeroLedgerBalances}
          badgeContent={nonZeroLedgerBalancesCount?.toString()}
          color="#3498db"
          hoverBackgroundColor="#3498db14"
          hoverColor="#2980b9"
          disabled={!nonZeroLedgerBalancesCount}
          href={`/non-zero-ledger-balances-redirect`}
          loading={loading}
        />
      )}
      {permissions.includes("Deposits") && (
        <DashboardBadge
          dashboardReportType={DashboardReportType.Deposits}
          badgeContent={dormantDepositsCount?.toString()}
          color="#5b7c99"
          hoverBackgroundColor="#eaf1f7"
          hoverColor="#405a73"
          disabled={!dormantDepositsCount}
          href={`/deposits`}
          loading={loading}
        />
      )}
      {permissions.includes("OutstandingChecks") && (
        <DashboardBadge
          dashboardReportType={DashboardReportType.OutstandingChecks}
          badgeContent={checksCount?.toString()}
          color="#00a8a8"
          hoverBackgroundColor="#00a8a814"
          hoverColor="#007d7d"
          disabled={!checksCount}
          href={"/checks"}
          loading={loading}
        />
      )}
      {permissions.includes("EscrowOverdue") && (
        <DashboardBadge
          dashboardReportType={DashboardReportType.EscrowOverdue}
          badgeContent={escrowOverdueOrdersCount?.toString()}
          color="#f39c12"
          hoverBackgroundColor="#f39c1214"
          hoverColor="#d68910"
          disabled={!escrowOverdueOrdersCount}
          href={`/escrow-ledgers`}
          loading={loading}
        />
      )}
      {permissions.includes("ExcessRecordingFunds") && (
        <DashboardBadge
          dashboardReportType={DashboardReportType.ExcessRecordingFunds}
          badgeContent={undefined}
          color="#4f46e5"
          hoverBackgroundColor="#eef2ff"
          hoverColor="#4338ca"
          disabled={false}
          href={`/recording-ledgers`}
        />
      )}
      {permissions.includes("PolicyIssues") && (
        <DashboardBadge
          dashboardReportType={DashboardReportType.PolicyIssues}
          badgeContent={policiesCount?.toString()}
          color="#e74c3c"
          hoverBackgroundColor="#e74c3c14"
          hoverColor="#c0392b"
          disabled={!policiesCount}
          href={`/policy-issues`}
        />
      )}
      {permissions.includes("PoliciesToSend") && (
        <DashboardBadge
          dashboardReportType={DashboardReportType.PoliciesToSend}
          badgeContent={policiesToSendCount?.toString()}
          color="#ae73d3"
          hoverBackgroundColor="#f2e9f9"
          hoverColor="#8d5fa9"
          disabled={!policiesToSendCount}
          href={`/policies-to-send`}
          loading={loading}
        />
      )}
    </Stack>
  );
}
